// CTH Diagrams (diagrams.coachtonyhockey.com): the rink editor (engine/) over a
// Dropbox folder (default /CTH-DB/Images/Diagrams), which is the file tree. One
// PNG per diagram, the drawing inside it as the `cthDiagram` chunk, so a file
// saved here shows as a plain picture anywhere (Obsidian notes embed it) and
// reopens editable here, in Film Room and in the CTH Apps web app.
//
// Layout: the file tree on the left, the library (a folder as cards) or the
// editor (stage + details panel) on the right. The URL hash is the place:
//   #/            the library root
//   #/f/<folder>  a folder, relative to the root
//   #/d/<file>    a diagram, relative to the root
import { createEditor } from '../engine/editor.js';
import { loadAssets } from '../engine/rink.js';
import { openAnimator, setAnimEnv } from '../engine/anim.js';
import { pngReadDiagram } from '../engine/png.js';
import { diagramBytes, stateFromBytes, canvasToBlob, safeName } from './files.js';
import * as dbx from './dropbox.js';
import * as drafts from './drafts.js';
import { h, icon, button, badge, toast, modal, confirm, prompt, pickFrom, menu, shortcutsModal, rinksModal, guideModal, isModalOpen } from './ui.js';
import RINK from '../assets/rink.png';
import NET from '../assets/shapes/net.png';
import COACH from '../assets/shapes/Co.png';
import PUCK from '../assets/shapes/puck.png';
import CONE from '../assets/shapes/cone.png';
import BORDER from '../assets/shapes/border.png';
import GUIDE from '../assets/ANIMATE.md';

// ---- settings ------------------------------------------------------------------

const SETTINGS_KEY = 'cthdw.settings.v1';
const DEFAULTS = {
  newName: 'Untitled diagram',
  newWithFurniture: true,      // nets and goalies on a new rink
  pictureScale: 1,             // 1 = 3200px wide, 0.75, 0.5
  theme: 'system',             // 'system' | 'light' | 'dark'
  librarySort: 'modified',     // 'modified' | 'name'
  cardSize: 'md',              // 'sm' | 'md' | 'lg'
  treeW: 272,                  // the file tree's width (drag its edge; double-click resets)
  treeOpen: true,              // the file tree (desktop); on a phone it is a drawer
  exportTo: 'download',        // 'download' | 'dropbox'
  exportFolder: '/CTH-DB/Images/Diagram Exports',
  expanded: [],                // folders open in the tree (relative paths)
  engine: { arrowHead: 'triangle', arrowPx: 8, revertToSelect: true, captionsInPictures: true },
};
let firstRun = true;
function loadSettings() {
  let s = {};
  try { const raw = localStorage.getItem(SETTINGS_KEY); firstRun = !raw; s = JSON.parse(raw || '{}') || {}; } catch (_) { s = {}; }
  return { ...DEFAULTS, ...s, engine: { ...DEFAULTS.engine, ...(s.engine || {}) } };
}
const settings = loadSettings();
let saveT = 0;
function saveSettings() {
  clearTimeout(saveT);
  saveT = setTimeout(() => { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch (_) { /* private window */ } }, 200);
  if (JSON.stringify(flatShared(settings)) !== sharedBase) shareSoon();
}

// ---- settings shared across devices ------------------------------------------------------
// The editor's settings (tool order, colours, tool keys, arrows, captions) and
// the new-diagram choices also live in <diagrams folder>/.cth-diagrams.json, so
// every browser and Notion embed (each has its own storage) works the same way.
// Merged setting by setting: one changed here since the last sync keeps this
// browser's value, every other one takes the file's, so two browsers never
// undo each other.
const SHARED_TOP = ['pictureScale', 'newName', 'newWithFurniture'];
function flatShared(s) {
  const out = {};
  for (const k of SHARED_TOP) out[k] = s[k];
  for (const [k, v] of Object.entries(s.engine || {})) out[`engine.${k}`] = v;
  return out;
}
function setFlat(s, k, v) { if (k.startsWith('engine.')) s.engine[k.slice(7)] = v; else s[k] = v; }
let sharedBase = settings.sharedBase || JSON.stringify(flatShared(settings));
let sharedRev = '';
const sharedPath = () => `${root()}/.cth-diagrams.json`;
/** The shared file, { missing: true } when it is not in Dropbox, or null when it could not be read. */
async function readShared() {
  try { const { bytes } = await dbx.download(sharedPath()); return JSON.parse(new TextDecoder().decode(bytes)); }
  catch (e) { return e?.status === 409 && /not_found/.test(e.body || '') ? { missing: true } : null; }
}
function mergeShared(remote) {
  const base = JSON.parse(sharedBase || '{}');
  const mine = flatShared(settings);
  const theirs = remote.settings || {};
  let changed = false; let mineAhead = false;
  for (const k of Object.keys(theirs)) {
    if (JSON.stringify(mine[k]) === JSON.stringify(theirs[k])) continue;
    if (k in base && JSON.stringify(mine[k]) !== JSON.stringify(base[k])) { mineAhead = true; continue; }
    setFlat(settings, k, theirs[k]); changed = true; // settings.engine is the object the editor reads live
  }
  settings.sharedSeen = remote.savedAt;
  sharedBase = JSON.stringify(flatShared(settings)); settings.sharedBase = sharedBase;
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch (_) { /* fine */ }
  return { changed, mineAhead };
}
let shareT = 0;
function shareSoon() { clearTimeout(shareT); shareT = setTimeout(() => void shareNow(), 800); }
async function shareNow() {
  clearTimeout(shareT);
  if (!dbx.connected()) return;
  const remote = await readShared();
  if (!remote || (!remote.missing && !remote.settings)) return; // unreadable: try again on the next change
  if (!remote.missing && remote.savedAt > (settings.sharedSeen || 0)) mergeShared(remote);
  try {
    const savedAt = Date.now();
    const flat = flatShared(settings);
    const meta = await dbx.upload(sharedPath(), new TextEncoder().encode(JSON.stringify({ savedAt, settings: flat })), { overwrite: true });
    sharedRev = meta.rev; settings.sharedSeen = savedAt; sharedBase = JSON.stringify(flat); settings.sharedBase = sharedBase;
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch (_) { /* fine */ }
  } catch (_) { /* offline: shared on the next change or refresh */ }
}
async function pullShared() {
  const remote = await readShared();
  if (!remote || !remote.settings || !(remote.savedAt > (settings.sharedSeen || 0))) return false;
  const { changed, mineAhead } = mergeShared(remote);
  if (mineAhead) shareSoon();
  return changed;
}
const pictureOpts = () => ({ scale: Number(settings.pictureScale) || 1, captions: settings.engine.captionsInPictures !== false });

function applyTheme() {
  const dark = settings.theme === 'dark' || (settings.theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.body.classList.toggle('theme-dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme);

// ---- paths -------------------------------------------------------------------------

const root = () => dbx.normPath(dbx.libraryRoot());
const abs = (rel) => dbx.normPath(`${root()}/${rel || ''}`);
const relOf = (absPath) => {
  const r = root().toLowerCase();
  const p = dbx.normPath(absPath);
  return p.toLowerCase().startsWith(`${r}/`) ? p.slice(r.length + 1) : p.toLowerCase() === r ? '' : p.replace(/^\//, '');
};
const parentOf = (p) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
const baseName = (p) => p.split('/').pop().replace(/\.png$/i, '');
const isPng = (name) => /\.png$/i.test(name);
const IMAGE_RE = /\.(png|jpe?g|webp)$/i;
const encodeRel = (rel) => rel.split('/').map(encodeURIComponent).join('/');
const decodeRel = (s) => s.split('/').map((x) => { try { return decodeURIComponent(x); } catch (_) { return x; } }).join('/');

// ---- the tree (one recursive Dropbox listing) ------------------------------------

let entries = null;        // null = not loaded; [] = empty; false = the root folder is missing
let byLower = new Map();   // lower-case relative path -> entry (with .rel)
let loadingTree = false;

// One refresh at a time; a request that arrives meanwhile runs once more after
// it, so a change is never missed. Resolves false when Dropbox was unreachable.
let treeAgain = false;
let treeRun = null;
async function refreshTree(opts = {}) {
  if (treeRun) { treeAgain = true; return treeRun; }
  treeRun = (async () => { let ok; do { treeAgain = false; ok = await refreshTreeOnce(opts); } while (treeAgain); return ok; })();
  try { return await treeRun; } finally { treeRun = null; }
}
async function refreshTreeOnce({ quiet = false } = {}) {
  loadingTree = true;
  try {
    const list = await dbx.listAll(root());
    const sh = (list || []).find((x) => x.name === '.cth-diagrams.json');
    if (sh && sh.rev !== sharedRev) { sharedRev = sh.rev; void pullShared(); }
    if (list === null) { entries = false; byLower = new Map(); }
    else {
      entries = list.map((e) => ({ ...e, rel: relOf(e.path) })).filter((e) => e.rel && !e.name.startsWith('.') && (e.dir || IMAGE_RE.test(e.name)));
      byLower = new Map(entries.map((e) => [e.rel.toLowerCase(), e]));
    }
    noteOk();
  } catch (e) {
    noteTrouble(e);
    if (e.message === 'NOT_CONNECTED') { if (!doc) render(); return false; }
    if (!quiet && !dbx.isNetworkError(e)) toast(`Could not read the Dropbox folder: ${e.message || e}`, 6000);
    if (!doc) renderMain();
    return false;
  } finally {
    loadingTree = false;
  }
  renderTree();
  if (route().kind === 'i' && resolveRoute().kind !== 'missing' && !(doc && route().kind === 'i' && (entries || []).find((x) => x.id === route().id && x.rel.toLowerCase() === doc.rel.toLowerCase()))) { void onRoute(); return true; }
  if (waitingFor && find(waitingFor)) { const r = route(); if (r.kind === 'd' && r.rel.toLowerCase() === waitingFor.toLowerCase()) { void onRoute(); return true; } }
  if (!doc) renderMain();
  else await syncOpenDoc();
  void flushDrafts();
  return true;
}

const find = (rel) => byLower.get(String(rel || '').toLowerCase()) || null;
const childrenOf = (folderRel) => (entries || []).filter((e) => parentOf(e.rel).toLowerCase() === folderRel.toLowerCase());
const foldersUnder = () => [''].concat((entries || []).filter((e) => e.dir).map((e) => e.rel).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
const diagramsUnder = (folderRel) => (entries || []).filter((e) => !e.dir && (!folderRel || e.rel.toLowerCase().startsWith(`${folderRel.toLowerCase()}/`)));

function uniqueRel(folderRel, base, ext = 'png', except = null) {
  for (let n = 1; ; n++) {
    const rel = `${folderRel ? `${folderRel}/` : ''}${base}${n > 1 ? ` ${n}` : ''}.${ext}`;
    const hit = find(rel);
    if (!hit || hit.rel === except) return rel;
  }
}

// ---- DOM shell ------------------------------------------------------------------------

const layer = h('div', { class: 'cthd-layer' });
const app = h('div', { class: 'cthd cthw-app' });
const tree = h('aside', { class: 'cthw-tree', 'aria-label': 'Diagrams folder' });
const main = h('main', { class: 'cthw-main' });
const scrim = h('div', { class: 'cthw-scrim' });
scrim.addEventListener('click', () => setTreeOpen(false));
app.append(tree, scrim, main);

const isNarrow = () => matchMedia('(max-width: 800px)').matches;
function setTreeOpen(open) {
  if (isNarrow()) app.classList.toggle('is-drawer-open', open);
  else { settings.treeOpen = open; saveSettings(); app.classList.toggle('is-tree-closed', !open); }
  requestAnimationFrame(() => ed?.refit());
}
/** Reload the whole app (works inside a Notion embed too); the open diagram saves first. */
const reloadBtn = () => button({ iconName: 'rotate-cw', size: 'icon-sm', variant: 'ghost', tip: 'Reload the app', onClick: async (e, b) => { b.disabled = true; try { await flush(); } catch (_) { /* its draft is kept */ } location.reload(); } });
const treeToggle = () => button({ iconName: 'panel-left', size: 'icon-sm', variant: 'ghost', tip: 'Show or hide the file tree', onClick: () => setTreeOpen(isNarrow() ? !app.classList.contains('is-drawer-open') : app.classList.contains('is-tree-closed')) });

// ---- routing --------------------------------------------------------------------------

function route() {
  const m = location.hash.match(/^#\/(f|d|i)\/(.*)$/);
  if (!m) return { kind: 'f', rel: '' };
  if (m[1] === 'i') return { kind: 'i', id: decodeURIComponent(m[2]), rel: '' };
  return { kind: m[1], rel: decodeRel(m[2]) };
}
// A #/i/<Dropbox id> link (what "Copy link" makes, for Notion embeds) names a
// file or folder by id, so it keeps working after a rename or a move.
function resolveRoute() {
  const r = route();
  if (r.kind !== 'i') return r;
  const e = (entries || []).find((x) => x.id === r.id);
  if (!e) return { kind: 'missing', id: r.id };
  return { kind: e.dir ? 'f' : 'd', rel: e.rel, viaId: true };
}
function go(kind, rel) {
  const hash = kind === 'f' && !rel ? '#/' : `#/${kind}/${encodeRel(rel)}`;
  if (location.hash !== hash) location.hash = hash; else onRoute();
}
let currentFolder = '';
let query = '';

async function onRoute() {
  if (!dbx.connected()) { render(); return; }
  const r = resolveRoute();
  if (isNarrow()) app.classList.remove('is-drawer-open');
  if (r.kind === 'missing') {
    await closeDoc();
    main.replaceChildren(h('div', { class: 'cthd-lib' }, h('div', { class: 'cthd-lib-body' }, entries === null
      ? emptyState('cloud', 'Reading your Dropbox folder', 'One moment.')
      : emptyState('search-x', 'This diagram is not in Dropbox any more', 'It was deleted, or moved out of the diagrams folder. If it is still syncing, it opens here by itself when it arrives.', button({ label: 'Open the library', variant: 'outline', onClick: () => go('f', '') })))));
    return;
  }
  if (r.kind === 'd') {
    currentFolder = parentOf(r.rel);
    if (!doc || doc.rel.toLowerCase() !== r.rel.toLowerCase()) await openDoc(r.rel);
  } else {
    await closeDoc();
    currentFolder = r.rel;
    renderMain();
  }
  renderTree();
}
window.addEventListener('hashchange', onRoute);

// ---- the file tree ------------------------------------------------------------------------

let treeFilter = '';
let filterOpen = false;   // the filter box shows after its button is pressed (or while it holds text)
let focusFilter = false;
function renderTree() {
  const scroll = tree.querySelector('.cthw-tree-list')?.scrollTop || 0;
  const hadFocus = document.activeElement?.classList.contains('cthw-tree-search');
  const expanded = new Set(settings.expanded.map((x) => x.toLowerCase()));
  const openRel = doc?.rel?.toLowerCase() || null;
  // keep the open diagram's folders open
  if (openRel) { let p = parentOf(doc.rel); while (p) { expanded.add(p.toLowerCase()); p = parentOf(p); } }

  const list = h('div', { class: 'cthw-tree-list', role: 'tree' });
  const q = treeFilter.trim().toLowerCase();
  const row = (e, depth) => {
    const active = e.dir ? (!doc && e.rel.toLowerCase() === currentFolder.toLowerCase()) : e.rel.toLowerCase() === openRel;
    const isOpen = e.dir && (q || expanded.has(e.rel.toLowerCase()));
    const r = h('div', { class: `cthw-row${active ? ' is-active' : ''}${e.dir ? ' is-dir' : ''}`, role: 'treeitem', tabindex: '0', 'aria-expanded': e.dir ? String(!!isOpen) : null, 'data-rel': e.dir ? e.rel.toLowerCase() : null, style: { paddingLeft: `${8 + depth * 14}px` }, title: e.rel });
    if (e.dir) {
      const chev = h('span', { class: 'cthw-chev' }, icon(isOpen ? 'chevron-down' : 'chevron-right'));
      chev.addEventListener('click', (ev) => { ev.stopPropagation(); toggleExpanded(e.rel); });
      r.append(chev, icon(isOpen ? 'folder-open' : 'folder'), h('span', { class: 'cthw-row-name', text: e.name }));
      r.addEventListener('click', () => { if (!expanded.has(e.rel.toLowerCase())) toggleExpanded(e.rel, true); go('f', e.rel); });
    } else {
      r.append(h('span', { class: 'cthw-chev' }), h('span', { class: 'cthw-row-name', text: baseName(e.name).replace(/\.(jpe?g|webp)$/i, '') })); // no file icon: the room goes to the name
      r.addEventListener('click', () => go('d', e.rel));
    }
    r.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); r.click(); }
      if (e.dir && ev.key === 'ArrowRight' && !isOpen) toggleExpanded(e.rel, true);
      if (e.dir && ev.key === 'ArrowLeft' && isOpen) toggleExpanded(e.rel, false);
    });
    r.addEventListener('contextmenu', (ev) => { ev.preventDefault(); (e.dir ? folderMenu : fileMenu)(ev, e); });
    wireDrag(r, e);
    if (e.dir) wireDrop(r, e.rel, { expand: true });
    else wireDrop(r, parentOf(e.rel), { mark: () => folderRow(parentOf(e.rel)) });
    list.append(r);
    if (isOpen) walk(e.rel, depth + 1);
  };
  const sortKids = (a, b) => (a.dir === b.dir ? a.name.localeCompare(b.name, undefined, { numeric: true }) : a.dir ? -1 : 1);
  const walk = (folderRel, depth) => {
    for (const e of childrenOf(folderRel).sort(sortKids)) {
      if (q && !e.dir && !e.rel.toLowerCase().includes(q)) continue;
      if (q && e.dir && !diagramsUnder(e.rel).some((d) => d.rel.toLowerCase().includes(q))) continue;
      row(e, depth);
    }
  };
  if (entries) walk('', 0);
  if (entries && !list.childElementCount) list.append(h('div', { class: 'cthw-tree-empty', text: q ? 'Nothing matches.' : 'No diagrams yet.' }));
  if (entries === null) list.append(h('div', { class: 'cthw-tree-empty', text: 'Loading...' }));

  const rootRow = h('button', { class: `cthw-row cthw-rootrow${!doc && !currentFolder ? ' is-active' : ''}`, type: 'button', title: root(), 'data-rel': '' }, icon('cloud'), h('span', { class: 'cthw-row-name', text: root().split('/').pop() || 'Dropbox' }));
  rootRow.addEventListener('click', () => go('f', ''));
  wireDrop(rootRow, '');
  wireDrop(list, '', { mark: rootRow }); // empty space under the rows = the top folder
  // scroll the list while dragging near its top or bottom edge
  list.addEventListener('dragover', (ev) => {
    if (!dragging) return;
    const b = list.getBoundingClientRect();
    if (ev.clientY < b.top + 36) list.scrollTop -= 12;
    else if (ev.clientY > b.bottom - 36) list.scrollTop += 12;
  }, true);

  const search = h('input', { class: 'cthd-input cthw-tree-search', type: 'search', placeholder: 'Filter', value: treeFilter, spellcheck: 'false', 'aria-label': 'Filter the file tree' });
  search.addEventListener('input', () => { treeFilter = search.value; renderTree(); });
  search.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); treeFilter = ''; filterOpen = false; renderTree(); } });

  tree.replaceChildren(
    h('div', { class: 'cthw-brand' }, h('span', { class: 'cthw-logo' }, icon('cth-rink')), h('span', { class: 'cthw-brand-name', text: 'CTH Diagrams' }),
      h('span', { class: 'cthd-grow' }),
      button({ iconName: 'refresh-cw', size: 'icon-sm', variant: 'ghost', tip: 'Refresh from Dropbox', onClick: async (e, b) => {
        b.classList.add('is-spinning');
        const ok = await refreshTree();
        setTimeout(() => b.classList.remove('is-spinning'), 300);
        toast(ok === false ? 'Could not reach Dropbox. Showing what was last loaded.' : 'Up to date with Dropbox.', 1800);
      } })),
    h('div', { class: 'cthw-tree-actions' },
      button({ label: 'New diagram', iconName: 'plus', variant: 'default', size: 'sm', cls: 'cthw-grow-btn', onClick: () => newDiagram(currentFolder) }),
      button({ iconName: 'folder-plus', size: 'icon-sm', variant: 'outline', tip: 'New folder', onClick: () => newFolder(currentFolder) }),
      button({ iconName: 'search', size: 'icon-sm', variant: filterOpen || treeFilter ? 'secondary' : 'outline', tip: filterOpen || treeFilter ? 'Close the filter' : 'Filter the tree', onClick: () => { if (filterOpen || treeFilter) { filterOpen = false; treeFilter = ''; } else { filterOpen = true; focusFilter = true; } renderTree(); } })),
    filterOpen || treeFilter ? h('div', { class: 'cthd-search cthw-tree-searchwrap' }, icon('search'), search) : '',
    rootRow,
    list,
    h('div', { class: 'cthw-tree-foot' },
      button({ label: 'Settings', iconName: 'settings', variant: 'ghost', size: 'sm', onClick: () => settingsModal() }),
      button({ iconName: 'keyboard', size: 'icon-sm', variant: 'ghost', tip: 'Keyboard shortcuts', onClick: () => shortcutsModal() })),
    treeResizer());
  const newList = tree.querySelector('.cthw-tree-list');
  newList.scrollTop = scroll;
  if (hadFocus || focusFilter) { focusFilter = false; search.focus(); search.setSelectionRange(search.value.length, search.value.length); }
  tree.querySelector('.cthw-row.is-active:not(.cthw-rootrow)')?.scrollIntoView?.({ block: 'nearest' });
}

/** The tree row of a folder ('' = the top folder), when it is showing. */
const folderRow = (rel) => tree.querySelector(`[data-rel="${CSS.escape(rel.toLowerCase())}"]`);

function toggleExpanded(rel, force) {
  const k = rel.toLowerCase();
  const has = settings.expanded.some((x) => x.toLowerCase() === k);
  const want = force ?? !has;
  settings.expanded = settings.expanded.filter((x) => x.toLowerCase() !== k);
  if (want) settings.expanded.push(rel);
  saveSettings();
  renderTree();
}

// ---- the library (a folder as cards) -------------------------------------------------------

const thumbs = new Map(); // path lower + rev -> object URL
let thumbRun = 0;

function renderMain() {
  if (doc) return;
  if (nameEditing(main)) { renderSkipped = true; return; } // renaming the folder in the top bar
  if (!dbx.connected()) { main.replaceChildren(connectCard()); return; }
  const scroll = main.querySelector('.cthd-lib-body')?.scrollTop || 0;
  const lib = h('div', { class: `cthd-lib is-size-${settings.cardSize}` });
  const body = h('div', { class: 'cthd-lib-body' });

  if (entries === false) {
    body.append(emptyState('folder-plus', `${root()} does not exist in your Dropbox yet`, 'Make your first diagram and the folder is created for you. You can also pick another folder in Settings.',
      button({ label: 'New diagram', iconName: 'plus', variant: 'default', onClick: () => newDiagram('') })));
    lib.append(libToolbar(0), body);
    main.replaceChildren(lib);
    return;
  }
  if (entries === null) { body.append(!navigator.onLine || !netOk ? emptyState('wifi-off', 'Offline', 'Your diagrams show up here as soon as the connection is back.') : emptyState('cloud', 'Reading your Dropbox folder', 'One moment.')); lib.append(libToolbar(0), body); main.replaceChildren(lib); return; }

  if (currentFolder && !find(currentFolder)?.dir) currentFolder = '';
  const q = query.trim().toLowerCase();
  let files;
  let folders = [];
  if (q) {
    files = diagramsUnder(currentFolder).filter((e) => (currentFolder ? e.rel.slice(currentFolder.length + 1) : e.rel).toLowerCase().includes(q));
  } else {
    const kids = childrenOf(currentFolder);
    files = kids.filter((e) => !e.dir);
    folders = kids.filter((e) => e.dir).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }
  files.sort((a, b) => (settings.librarySort === 'name' ? a.name.localeCompare(b.name, undefined, { numeric: true }) : b.modified - a.modified));

  if (folders.length) {
    body.append(h('div', { class: 'cthd-lib-section', text: 'Folders' }));
    const grid = h('div', { class: 'cthd-folders' });
    for (const f of folders) {
      const tile = h('button', { class: 'cthd-folder', type: 'button', 'aria-label': `Open ${f.name}` }, icon('folder'), h('span', { class: 'cthd-folder-name', text: f.name }), h('span', { class: 'cthd-folder-count', text: String(diagramsUnder(f.rel).length) }));
      tile.addEventListener('click', () => go('f', f.rel));
      tile.addEventListener('contextmenu', (e) => { e.preventDefault(); folderMenu(e, f); });
      wireDrag(tile, f);
      wireDrop(tile, f.rel, { expand: false });
      grid.append(tile);
    }
    body.append(grid);
  }
  if (files.length) {
    if (folders.length) body.append(h('div', { class: 'cthd-lib-section', text: q ? 'Results' : 'Diagrams' }));
    const grid = h('div', { class: 'cthd-grid' });
    for (const f of files) grid.append(card(f, !!q));
    body.append(grid);
  } else if (q) {
    body.append(emptyState('search-x', 'No diagrams match', `Nothing here is called anything like "${query.trim()}".`, button({ label: 'Clear search', variant: 'outline', onClick: () => { query = ''; renderMain(); } })));
  } else if (!folders.length) {
    body.append(emptyState('cth-rink', currentFolder ? 'This folder is empty' : 'No diagrams yet',
      'Make one, or drop PNG, JPEG or WebP pictures here. A PNG from CTH Diagrams or Film Room opens fully editable.',
      h('div', { class: 'cthd-row' },
        button({ label: 'New diagram', iconName: 'plus', variant: 'default', onClick: () => newDiagram(currentFolder) }),
        button({ label: 'Import pictures', iconName: 'upload', variant: 'outline', onClick: () => pickImport(currentFolder) }))));
  }
  lib.append(libToolbar(files.length), body);
  wireDrop(lib, currentFolder);
  main.replaceChildren(lib);
  body.scrollTop = scroll;
  fillThumbs();
}

function emptyState(ic, title, desc, action) {
  return h('div', { class: 'cthd-empty' }, h('div', { class: 'cthd-empty-icon' }, icon(ic)), h('div', { class: 'cthd-empty-title', text: title }), h('div', { class: 'cthd-empty-desc', text: desc }), action || null);
}

function crumbs(folderRel, tail = null) {
  const nav = h('nav', { class: 'cthd-crumbs', 'aria-label': 'Folder' });
  const parts = [[root().split('/').pop() || 'Dropbox', '']];
  let acc = '';
  for (const seg of folderRel ? folderRel.split('/') : []) { acc = acc ? `${acc}/${seg}` : seg; parts.push([seg, acc]); }
  if (tail) parts.push([tail, null]);
  parts.forEach(([label, rel], i) => {
    if (i) nav.append(icon('chevron-right', 'cthd-crumb-sep'));
    const last = i === parts.length - 1;
    const b = h('button', { class: `cthd-crumb${last ? ' is-current' : ''}`, type: 'button', text: label, 'aria-current': last ? 'page' : null });
    if (!last && rel != null) b.addEventListener('click', () => go('f', rel));
    if (rel != null) wireDrop(b, rel);
    nav.append(b);
  });
  return nav;
}

/** The library's breadcrumbs: click the current folder's name to rename it. */
function folderCrumbs() {
  const nav = crumbs(currentFolder);
  const f = currentFolder && find(currentFolder);
  const cur = nav.querySelector('.cthd-crumb.is-current');
  if (f && cur) {
    cur.classList.add('is-renamable');
    cur.title = 'Click to rename this folder';
    cur.addEventListener('click', () => editName(cur, f.name, (want) => relocateFolder(f, joinRel(parentOf(f.rel), safeName(want, f.name)), 'rename'), 'Folder name'));
  }
  return nav;
}

function libToolbar(count) {
  const search = h('input', { class: 'cthd-input', type: 'search', placeholder: 'Search diagrams', value: query, spellcheck: 'false', 'aria-label': 'Search diagrams in this folder and every folder under it' });
  search.addEventListener('input', () => { query = search.value; renderMain(); const s = main.querySelector('.cthd-search input'); s?.focus(); s?.setSelectionRange(query.length, query.length); });
  search.addEventListener('keydown', (e) => { if (e.key === 'Escape' && query) { e.preventDefault(); query = ''; renderMain(); } });
  const sortLabel = settings.librarySort === 'name' ? 'Name A to Z' : 'Last modified';
  return h('div', { class: 'cthd-lib-toolbar' },
    h('div', { class: 'cthd-lib-left' }, treeToggle(), reloadBtn(), folderCrumbs(), count ? badge(String(count), 'secondary') : null),
    h('div', { class: 'cthd-lib-right' },
      h('div', { class: 'cthd-search' }, icon('search'), search),
      button({ label: sortLabel, iconName: 'arrow-down-up', variant: 'outline', size: 'sm', tip: 'Sort', onClick: (e) => menu(e, [
        { label: 'Last modified', checked: settings.librarySort === 'modified', run: () => { settings.librarySort = 'modified'; saveSettings(); renderMain(); } },
        { label: 'Name A to Z', checked: settings.librarySort === 'name', run: () => { settings.librarySort = 'name'; saveSettings(); renderMain(); } },
      ]) }),
      button({ iconName: 'link', tip: currentFolder ? 'Copy Notion embed link for this folder' : 'Copy Notion embed link for the library', size: 'icon-sm', variant: 'outline', onClick: () => { const f = currentFolder && find(currentFolder); copyText(f?.id ? `${location.origin}/#/i/${encodeURIComponent(f.id)}` : `${location.origin}/#/${currentFolder ? `f/${encodeRel(currentFolder)}` : ''}`, copiedLinkMsg()); } }),
      dbx.embedded ? button({ iconName: 'external-link', tip: 'Open in full window', size: 'icon-sm', variant: 'outline', onClick: () => openFullWindow() }) : null,
      button({ iconName: 'folder-plus', tip: 'New folder', size: 'icon-sm', variant: 'outline', onClick: () => newFolder(currentFolder) }),
      button({ iconName: 'upload', tip: 'Import pictures (PNG, JPEG, WebP)', size: 'icon-sm', variant: 'outline', onClick: () => pickImport(currentFolder) }),
      button({ label: 'New diagram', iconName: 'plus', variant: 'default', size: 'sm', onClick: () => newDiagram(currentFolder) })));
}

const when = (t) => {
  if (!t) return '';
  const d = new Date(t);
  const now = new Date();
  const days = Math.floor((now - d) / 864e5);
  if (d.toDateString() === now.toDateString()) return `Today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  if (days < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
};

/**
 * Is a picture of this size a stack of 2 or more rinks (a sequence)? Its card
 * then shows only the first rink (Tony, 2026-09-23). Measured in rink units:
 * n rinks of 1600 with 60 gaps, plus a 150 caption band per rink when captioned.
 */
function isRinkStack(w, h) {
  if (!w || !h) return false;
  const k = (h * 3200) / w;
  for (let n = 2; n <= 20; n++) if (Math.abs(k - (n * 1660 - 60)) < 60 || Math.abs(k - (n * 1810 - 60)) < 80) return true;
  return false;
}

function card(e, showFolder) {
  const img = h('img', { class: 'cthd-thumb', alt: '', draggable: 'false' });
  img.addEventListener('load', () => img.classList.toggle('is-seq', isRinkStack(img.naturalWidth, img.naturalHeight)));
  img.__key = `${e.lower}@${e.rev}`;
  img.__path = e.path;
  const sub = [showFolder ? parentOf(e.rel).split('/').pop() || null : null, when(e.modified)].filter(Boolean).join(' · ');
  const c = h('div', { class: 'cthd-card', tabindex: '0', role: 'button', 'aria-label': baseName(e.name) },
    h('div', { class: 'cthd-card-pic' }, img),
    h('div', { class: 'cthd-card-meta' }, h('div', { class: 'cthd-card-name', text: baseName(e.name) }), h('div', { class: 'cthd-card-sub', text: sub })));
  c.querySelector('.cthd-card-pic').append(button({ iconName: 'more-horizontal', tip: 'More', size: 'icon-sm', variant: 'secondary', cls: 'cthd-card-more', onClick: (ev) => { ev.stopPropagation(); fileMenu(ev, e); } }));
  c.addEventListener('click', () => go('d', e.rel));
  c.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go('d', e.rel); } });
  c.addEventListener('contextmenu', (ev) => { ev.preventDefault(); fileMenu(ev, e); });
  wireDrag(c, e);
  return c;
}

/** Card pictures: Dropbox thumbnails, four at a time, cached by revision. */
async function fillThumbs() {
  const run = ++thumbRun;
  const imgs = [...main.querySelectorAll('img.cthd-thumb')];
  const todo = [];
  for (const img of imgs) { const hit = thumbs.get(img.__key); if (hit) img.src = hit; else todo.push(img); }
  const worker = async () => {
    while (todo.length && run === thumbRun) {
      const img = todo.shift();
      try {
        const blob = await dbx.thumbnail(img.__path);
        const url = URL.createObjectURL(blob);
        thumbs.set(img.__key, url);
        img.src = url;
      } catch (_) {
        // Dropbox makes no thumbnail over 20 MB (big photo-backed diagrams): show the PNG itself
        try { const url = await dbx.tempLink(img.__path); thumbs.set(img.__key, url); img.src = url; }
        catch (__) { img.closest('.cthd-card-pic')?.classList.add('is-broken'); }
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
}

// ---- menus and file actions ----------------------------------------------------------------

function fileMenu(e, f) {
  menu(e, [
    { label: 'Open', icon: 'cth-rink', run: () => go('d', f.rel) },
    { label: 'Copy Notion embed link', icon: 'link', run: () => copyText(appLink(f.rel, f.id), copiedLinkMsg()) },
    'sep',
    { label: 'Rename', icon: 'pencil', run: () => renameFile(f) },
    { label: 'Move to folder', icon: 'folder-input', run: () => moveFile(f) },
    { label: 'Duplicate', icon: 'copy-plus', run: () => duplicateFile(f) },
    { label: 'Download', icon: 'download', run: () => downloadFile(f) },
    'sep',
    { label: 'Delete', icon: 'trash-2', danger: true, run: () => deleteFile(f) },
  ]);
}

function folderMenu(e, f) {
  menu(e, [
    { label: 'Open', icon: 'folder-open', run: () => go('f', f.rel) },
    { label: 'New diagram here', icon: 'plus', run: () => newDiagram(f.rel) },
    { label: 'New folder here', icon: 'folder-plus', run: () => newFolder(f.rel) },
    'sep',
    { label: 'Rename folder', icon: 'pencil', run: () => renameFolder(f) },
    { label: 'Move to folder', icon: 'folder-input', run: () => moveFolder(f) },
    { label: 'Delete folder', icon: 'trash-2', danger: true, run: () => deleteFolder(f) },
  ]);
}

// Links name files by their Dropbox id when it is known, so a link pasted into
// Notion as an embed keeps working after the diagram is renamed or moved.
function appLink(rel, id) {
  const e = id ? { id } : find(rel);
  return e?.id ? `${location.origin}/#/i/${encodeURIComponent(e.id)}` : `${location.origin}/#/d/${encodeRel(rel)}`;
}
const fullWindowUrl = () => `${location.origin}/${location.hash || '#/'}`;
function openFullWindow() { window.open(fullWindowUrl(), '_blank', 'noopener'); }
/** In an embed, some browser features (clipboard, downloads) can be switched off by the page around it. */
function blockedInEmbed(what) {
  const frag = document.createDocumentFragment();
  frag.append(`${what} is blocked inside this embed. `);
  const b = h('button', { class: 'cthw-toast-btn', type: 'button', text: 'Open in full window' });
  b.addEventListener('click', openFullWindow);
  frag.append(b);
  toast(frag, 8000);
}
const copiedLinkMsg = () => (dbx.embedded ? 'Link copied.' : 'Link copied. Paste it in Notion and choose Embed; it keeps working if the diagram is renamed or moved.');
async function copyText(t, msg) {
  try { await navigator.clipboard.writeText(t); toast(msg); }
  catch (_) {
    if (dbx.embedded) { window.prompt('Copy this:', t); return; } // the clipboard is often off in embeds; a prompt still lets you copy
    toast('Could not copy. Your browser blocked the clipboard.');
  }
}

function blankState() {
  const st = { v: 1, w: 3200, h: 1600, bg: null, seq: 1, elements: [] };
  if (settings.newWithFurniture) {
    const tmp = createEditor({ root: document.createElement('div'), layer, getSettings: () => settings.engine, setSettings: () => {} });
    st.elements = tmp.rinkFurniture(0);
  }
  return st;
}

async function newDiagram(folderRel) {
  try {
    const rel = uniqueRel(folderRel, safeName(settings.newName));
    const bytes = await diagramBytes(blankState(), pictureOpts());
    const meta = await dbx.upload(abs(rel), bytes);
    addEntry(meta);
    pendingRename = true;
    go('d', relOf(meta.path));
  } catch (e) { reportError('make a new diagram', e); }
}

async function newFolder(parentRel) {
  const name = await prompt({ title: 'New folder', label: parentRel ? `Inside ${parentRel}` : `Inside ${root().split('/').pop()}`, placeholder: 'Folder name', action: 'Create' });
  if (name == null) return;
  const clean = safeName(name, '');
  if (!clean) return;
  const rel = parentRel ? `${parentRel}/${clean}` : clean;
  if (find(rel)) { toast('There is already something with that name here.'); return; }
  try {
    await dbx.createFolder(abs(rel));
    toggleExpanded(parentRel || rel, true);
    await refreshTree();
    go('f', rel);
  } catch (e) { reportError('make the folder', e); }
}

async function renameFile(f) {
  if (doc && doc.rel.toLowerCase() === f.rel.toLowerCase()) { startRename(); return; }
  const want = await prompt({ title: 'Rename diagram', value: baseName(f.name), action: 'Rename' });
  if (want == null) return;
  await doRename(f, safeName(want, baseName(f.name)));
}

async function doRename(f, newBase) {
  if (newBase === baseName(f.name)) return;
  const ext = (f.name.match(/\.([^.]+)$/) || [, 'png'])[1];
  const rel = uniqueRel(parentOf(f.rel), newBase, ext, f.rel);
  const got = baseName(rel).replace(/\.[^.]+$/, '');
  await moveTo(f, rel, got === newBase ? `Renamed to ${newBase}.` : `There is already a diagram called "${newBase}" here, so this one is now "${got}".`);
}

async function moveTo(f, rel, msg) {
  const openHere = doc && doc.rel.toLowerCase() === f.rel.toLowerCase();
  try {
    if (openHere) await flush();
    const meta = await dbx.move(f.path, abs(rel));
    if (openHere) { doc.rel = relOf(meta.path); doc.path = meta.path; doc.rev = meta.rev; doc.drill.name = baseName(meta.name); history.replaceState(null, '', `#/d/${encodeRel(doc.rel)}`); }
    await refreshTree({ quiet: true });
    if (openHere) paintTop();
    if (msg) toast(msg);
    return relOf(meta.path);
  } catch (e) { reportError('rename or move it', e); return null; }
}

async function moveFile(f) {
  const folders = foldersUnder().filter((r) => r.toLowerCase() !== parentOf(f.rel).toLowerCase());
  if (!folders.length) { toast('There is no other folder yet. Make one first.'); return; }
  const pick = await pickFrom(`Move "${baseName(f.name)}" to`, folders.map((r) => ({ label: r || root().split('/').pop(), value: r })));
  if (pick == null) return;
  const ext = (f.name.match(/\.([^.]+)$/) || [, 'png'])[1];
  await moveTo(f, uniqueRel(pick, baseName(f.name), ext), `Moved to ${pick || root().split('/').pop()}.`);
}

async function duplicateFile(f) {
  try {
    if (doc && doc.rel.toLowerCase() === f.rel.toLowerCase()) await flush();
    const rel = uniqueRel(parentOf(f.rel), `${baseName(f.name)} copy`);
    const meta = await dbx.copy(f.path, abs(rel));
    addEntry(meta);
    pendingRename = true;
    go('d', relOf(meta.path));
  } catch (e) { reportError('duplicate it', e); }
}

async function downloadFile(f) {
  try {
    // an embed may not be allowed to download: open the picture in a tab instead, where it can be saved
    if (dbx.embedded) { window.open(await dbx.tempLink(f.path), '_blank', 'noopener'); return; }
    const { bytes } = await dbx.download(f.path);
    saveBlob(new Blob([bytes], { type: 'image/png' }), f.name);
  } catch (e) { reportError('download it', e); }
}

async function deleteFile(f) {
  const ok = await confirm({ title: `Delete "${baseName(f.name)}"?`, body: 'Dropbox keeps deleted files, so you can restore it from dropbox.com (Deleted files) for at least 30 days. Obsidian notes that embed it will show a missing image until then.', action: 'Delete' });
  if (!ok) return;
  try {
    if (doc && doc.rel.toLowerCase() === f.rel.toLowerCase()) { ed?.cancelAutosave(); await closeDoc(true); }
    await dbx.remove(f.path);
    await refreshTree({ quiet: true });
    toast(`Deleted ${baseName(f.name)}.`);
    if (route().kind === 'd' && route().rel.toLowerCase() === f.rel.toLowerCase()) go('f', parentOf(f.rel));
  } catch (e) { reportError('delete it', e); }
}

async function renameFolder(f) {
  const want = await prompt({ title: 'Rename folder', value: f.name, action: 'Rename' });
  if (want == null) return;
  await relocateFolder(f, joinRel(parentOf(f.rel), safeName(want, f.name)), 'rename');
}

async function moveFolder(f) {
  const lo = f.rel.toLowerCase();
  const folders = foldersUnder().filter((r) => r.toLowerCase() !== lo && !r.toLowerCase().startsWith(`${lo}/`) && r.toLowerCase() !== parentOf(f.rel).toLowerCase());
  if (!folders.length) { toast('There is no other folder to move it to yet.'); return; }
  const pick = await pickFrom(`Move the folder "${f.name}" to`, folders.map((r) => ({ label: r || root().split('/').pop(), value: r })));
  if (pick == null) return;
  await relocateFolder(f, joinRel(pick, f.name), 'move');
}

/** Rename or move a folder (and everything in it). Returns the new relative path, or null. */
async function relocateFolder(f, rel, why) {
  if (!f || !rel || rel === f.rel) return null;
  const clash = find(rel);
  if (clash && rel.toLowerCase() !== f.rel.toLowerCase()) { toast(`There is already ${clash.dir ? 'a folder' : 'a diagram'} called "${baseName(rel)}" in ${parentOf(rel) || root().split('/').pop()}.`); return null; }
  const lo = f.rel.toLowerCase();
  const inside = (r) => r.toLowerCase() === lo || r.toLowerCase().startsWith(`${lo}/`);
  const swap = (r) => rel + r.slice(f.rel.length);
  try {
    if (doc && inside(doc.rel)) await flush();
    await dbx.move(f.path, abs(rel));
    if (doc && inside(doc.rel)) { doc.rel = swap(doc.rel); doc.path = abs(doc.rel); history.replaceState(null, '', `#/d/${encodeRel(doc.rel)}`); }
    settings.expanded = settings.expanded.map((x) => (inside(x) ? swap(x) : x));
    if (why === 'move' && parentOf(rel)) settings.expanded.push(parentOf(rel));
    saveSettings();
    if (currentFolder && inside(currentFolder)) { currentFolder = swap(currentFolder); if (!doc) history.replaceState(null, '', `#/f/${encodeRel(currentFolder)}`); }
    await refreshTree({ quiet: true });
    if (doc) paintTop();
    if (why === 'rename') toast(`Renamed to ${baseName(rel)}.`);
    else if (why === 'move') toast(`Moved ${f.name} to ${parentOf(rel) || root().split('/').pop()}.`);
    else if (why === 'undo') toast('Moved back.');
    return rel;
  } catch (e) { reportError(why === 'rename' ? 'rename the folder' : 'move the folder', e); return null; }
}

async function deleteFolder(f) {
  const n = diagramsUnder(f.rel).length;
  const ok = await confirm({ title: `Delete the folder "${f.name}"?`, body: `${n ? `It holds ${n} diagram${n === 1 ? '' : 's'}. ` : ''}Dropbox keeps deleted files, so you can restore it from dropbox.com for at least 30 days.`, action: 'Delete folder' });
  if (!ok) return;
  try {
    if (doc && doc.rel.toLowerCase().startsWith(`${f.rel.toLowerCase()}/`)) { ed?.cancelAutosave(); await closeDoc(true); }
    await dbx.remove(f.path);
    if (currentFolder.toLowerCase().startsWith(f.rel.toLowerCase())) currentFolder = parentOf(f.rel);
    await refreshTree({ quiet: true });
    go('f', currentFolder);
  } catch (e) { reportError('delete the folder', e); }
}

function addEntry(meta) {
  const e = { ...meta, rel: relOf(meta.path) };
  if (!entries) entries = [];
  entries = entries.filter((x) => x.rel.toLowerCase() !== e.rel.toLowerCase()).concat(e);
  byLower.set(e.rel.toLowerCase(), e);
  // make sure its folders are known too (a new diagram in a missing root)
  let p = parentOf(e.rel);
  while (p) { if (!find(p)) { const d = { dir: true, name: p.split('/').pop(), path: abs(p), lower: abs(p).toLowerCase(), rel: p, size: 0, rev: '', modified: 0, created: 0 }; entries.push(d); byLower.set(p.toLowerCase(), d); } p = parentOf(p); }
}

// ---- import ----------------------------------------------------------------------------------

function pickImport(folderRel) {
  const i = h('input', { type: 'file', accept: 'image/png,image/jpeg,image/webp', multiple: true, style: { position: 'fixed', left: '-9999px' } });
  i.addEventListener('change', () => { importFiles(folderRel, [...(i.files || [])]); i.remove(); });
  document.body.append(i);
  i.click();
}

async function importFiles(folderRel, files) {
  const made = [];
  const failed = [];
  for (const f of files) {
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'webp'].includes(ext)) { failed.push(`${f.name} (not a PNG, JPEG or WebP)`); continue; }
    try {
      const u8 = new Uint8Array(await f.arrayBuffer());
      const base = safeName(f.name.replace(/\.[^.]+$/, ''));
      let bytes = u8;
      if (!(ext === 'png' && pngReadDiagram(u8))) {
        const { state } = await stateFromBytes(u8, ext);
        bytes = await diagramBytes(state, pictureOpts());
      }
      const meta = await dbx.upload(abs(uniqueRel(folderRel, base)), bytes);
      addEntry(meta);
      made.push(meta);
    } catch (e) { failed.push(`${f.name} (${e.message || 'failed'})`); }
  }
  if (made.length) toast(`Imported ${made.length} picture${made.length === 1 ? '' : 's'}.`);
  if (failed.length) toast(`Could not import: ${failed.join(', ')}`, 8000);
  await refreshTree({ quiet: true });
  if (made.length === 1 && files.length === 1) go('d', relOf(made[0].path));
}

// ---- drag and drop -----------------------------------------------------------------------
// Pictures from the desktop dropped on a folder are imported there. A diagram
// or folder dragged (tree row, card, folder tile) onto a folder (tree row,
// folder tile, breadcrumb, the library around the cards, or empty space in the
// tree = the top folder) moves there. A diagram row stands for its folder, and
// its folder's row lights up. Hovering a closed folder opens it. The toast
// after a move has Undo.
const MOVE_TYPE = 'application/x-cth-move';
let dragging = null;     // the entry being dragged
let expandTimer = 0;
let expandRel = null;
function wireDrag(el, entry) {
  el.draggable = true;
  el.addEventListener('dragstart', (e) => {
    dragging = entry;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(MOVE_TYPE, entry.rel);
    document.documentElement.classList.add('cthw-is-dragging');
    requestAnimationFrame(() => el.classList.add('is-drag-source'));
  });
  el.addEventListener('dragend', endDrag);
}
function endDrag() {
  dragging = null;
  clearTimeout(expandTimer); expandRel = null;
  document.documentElement.classList.remove('cthw-is-dragging');
  document.querySelectorAll('.cthw-dropping, .is-drag-source').forEach((x) => x.classList.remove('cthw-dropping', 'is-drag-source'));
}
/** Can `item` move into the folder `folderRel`? (not into itself, not where it already is) */
function canDrop(item, folderRel) {
  if (!item) return false;
  const to = folderRel.toLowerCase();
  if (parentOf(item.rel).toLowerCase() === to) return false;
  if (item.dir) { const lo = item.rel.toLowerCase(); return to !== lo && !to.startsWith(`${lo}/`); }
  return true;
}
function markDrop(el) {
  document.querySelectorAll('.cthw-dropping').forEach((x) => { if (x !== el) x.classList.remove('cthw-dropping'); });
  el?.classList.add('cthw-dropping');
}
/** Make `el` a drop target for the folder `folderRel`. `mark` is the element that lights up (default el). */
function wireDrop(el, folderRel, { mark = null, expand = false } = {}) {
  const kind = (e) => { const t = [...(e.dataTransfer?.types || [])]; return t.includes(MOVE_TYPE) ? (dragging ? 'move' : null) : t.includes('Files') ? 'files' : null; };
  const target = () => (typeof mark === 'function' ? mark() : mark) || el;
  el.addEventListener('dragover', (e) => {
    const k = kind(e);
    if (!k) return;
    e.stopPropagation();
    if (k === 'move' && !canDrop(dragging, folderRel)) { e.dataTransfer.dropEffect = 'none'; markDrop(null); return; }
    e.preventDefault();
    e.dataTransfer.dropEffect = k === 'move' ? 'move' : 'copy';
    markDrop(target());
    if (k === 'move' && expand && expandRel !== folderRel) {
      clearTimeout(expandTimer); expandRel = folderRel;
      if (!settings.expanded.some((x) => x.toLowerCase() === folderRel.toLowerCase())) expandTimer = setTimeout(() => toggleExpanded(folderRel, true), 650);
    } else if (!expand) { clearTimeout(expandTimer); expandRel = null; }
  });
  el.addEventListener('dragleave', (e) => { if (!el.contains(e.relatedTarget)) target()?.classList.remove('cthw-dropping'); });
  el.addEventListener('drop', (e) => {
    const k = kind(e);
    if (!k) return;
    e.preventDefault(); e.stopPropagation();
    const item = dragging;
    endDrag();
    if (k === 'files') importFiles(folderRel, [...e.dataTransfer.files]);
    else if (canDrop(item, folderRel)) void moveByDrop(item, folderRel);
  });
}
async function moveByDrop(item, folderRel) {
  const from = item.rel;
  const where = folderRel ? folderRel.split('/').pop() : root().split('/').pop();
  let to;
  if (item.dir) {
    to = await relocateFolder(item, joinRel(folderRel, item.name), 'drop');
    if (to) undoToast(`Moved ${item.name} to ${where}.`, () => { const f = find(to); if (f) void relocateFolder(f, from, 'undo'); });
  } else {
    const ext = (item.name.match(/\.([^.]+)$/) || [, 'png'])[1];
    to = await moveTo(item, uniqueRel(folderRel, baseName(item.name), ext), null);
    if (to) {
      const name = baseName(item.name).replace(/\.[^.]+$/, '');
      undoToast(`Moved ${name} to ${where}.`, () => { const f = find(to); if (f) void moveTo(f, uniqueRel(parentOf(from), baseName(from).replace(/\.[^.]+$/, ''), ext, to), 'Moved back.'); });
    }
  }
}
function undoToast(text, undo) {
  const frag = document.createDocumentFragment();
  frag.append(`${text} `);
  const b = h('button', { class: 'cthw-toast-btn', type: 'button', text: 'Undo' });
  b.addEventListener('click', () => { if (b.disabled) return; b.disabled = true; undo(); });
  frag.append(b);
  toast(frag, 8000);
}
const joinRel = (folderRel, name) => (folderRel ? `${folderRel}/${name}` : name);

// ---- renaming in place (the top bar, the details panel) -------------------------------------

let renderSkipped = false;
const nameEditing = (box) => !!box && document.activeElement?.classList.contains('cthw-name-input') && box.contains(document.activeElement);
const measure = document.createElement('canvas').getContext('2d');
/** Swap `el` for a text field holding `current`: Enter or clicking away saves (save(value)), Escape cancels. */
function editName(el, current, save, label, { cls = '', fit = true } = {}) {
  if (!el?.isConnected) return;
  const input = h('input', { class: `cthd-input cthw-name-input ${cls}`.trim(), type: 'text', value: current, spellcheck: 'false', autocomplete: 'off', 'aria-label': label });
  el.replaceWith(input);
  const size = () => {
    if (!fit) return;
    const cs = getComputedStyle(input);
    measure.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    input.style.width = `${Math.round(Math.min(Math.max(measure.measureText(input.value || ' ').width + 34, 140), Math.max(200, innerWidth * 0.5)))}px`;
  };
  size();
  input.focus();
  input.select();
  let done = false;
  const finish = async (ok) => {
    if (done) return;
    done = true;
    const want = input.value.trim();
    if (input.isConnected) input.replaceWith(el);
    if (ok && want && want !== current) await save(want);
    if (renderSkipped) { renderSkipped = false; if (doc) paintTop(); else renderMain(); }
  };
  input.addEventListener('input', size);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); void finish(true); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); void finish(false); }
  });
  input.addEventListener('blur', () => void finish(true));
}

// ---- the file tree's width ------------------------------------------------------------------

function setTreeW(w) {
  const max = Math.max(240, Math.min(640, innerWidth - 360));
  settings.treeW = Math.round(Math.min(max, Math.max(200, w)));
  document.documentElement.style.setProperty('--tree-w', `${settings.treeW}px`);
  saveSettings();
}
function treeResizer() {
  const grip = h('div', { class: 'cthw-tree-resize', role: 'separator', 'aria-orientation': 'vertical', 'aria-label': 'Resize the file tree', title: 'Drag to resize. Double-click to reset.' });
  grip.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    const x0 = e.clientX; const w0 = tree.getBoundingClientRect().width;
    document.documentElement.classList.add('cthw-resizing');
    const move = (ev) => setTreeW(w0 + ev.clientX - x0);
    const up = () => { grip.removeEventListener('pointermove', move); document.documentElement.classList.remove('cthw-resizing'); requestAnimationFrame(() => ed?.refit()); };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up, { once: true });
    grip.addEventListener('pointercancel', up, { once: true });
  });
  grip.addEventListener('dblclick', () => { setTreeW(DEFAULTS.treeW); requestAnimationFrame(() => ed?.refit()); });
  return grip;
}

// ---- the editor ----------------------------------------------------------------------------

const EDITOR_MARKUP = `
  <div class="ed-main">
    <div class="ed-stagewrap" id="edStageWrap">
      <div class="ed-zoom" id="edZoom">
        <div class="ed-stage" id="edStage">
          <svg id="edSvg" xmlns="http://www.w3.org/2000/svg">
            <g id="edBgG"></g>
            <g id="edEls"></g>
            <g id="edUi"></g>
          </svg>
        </div>
        <div class="ed-addrow">
          <button class="ed-addbar" id="edAddBar" hidden>+ Add rink</button>
          <button class="ed-addbar" id="edDupBar" hidden aria-label="Add a new rink that copies the one above and everything on it">Duplicate rink</button>
        </div>
      </div>
    </div>
  </div>
  <div class="tb" id="edBar"></div>`;

let ed = null;
let doc = null;          // { rel, path, rev, drill, carried, frames, size, modified }
let edRoot = null;
let topbar = null;
let status = null;
let ro = null;
let tipObs = null;
let writeChain = Promise.resolve();
let writing = 0;
let pendingRename = false;
let openSeq = 0;

async function openDoc(rel) {
  const seq = ++openSeq;
  await closeDoc();
  const e = find(rel);
  const ext = (rel.split('.').pop() || 'png').toLowerCase();
  const isPic = ext !== 'png';
  const key = draftKeyOf(rel, isPic);
  // Edits kept on this device go to Dropbox first, so the file opened is current.
  let kept = await drafts.get(key).catch(() => null);
  if (kept && dbx.connected() && navigator.onLine) { await flushDrafts(); kept = await drafts.get(key).catch(() => null); }
  if (seq !== openSeq) return;
  if (entries && !e && !kept) {
    waitingFor = rel;
    main.replaceChildren(h('div', { class: 'cthd-lib' }, h('div', { class: 'cthd-lib-body' }, emptyState('search-x', 'That diagram is not in Dropbox yet', `Nothing called "${rel}" is in ${root()}. If it is still syncing, it opens here by itself when it arrives. Otherwise it was renamed, moved or deleted.`, button({ label: 'Open the library', variant: 'outline', onClick: () => go('f', parentOf(rel)) })))));
    return;
  }
  waitingFor = null;
  main.replaceChildren(h('div', { class: 'cthd-lib' }, h('div', { class: 'cthd-lib-body' }, emptyState('cth-rink', 'Opening', baseName(rel)))));
  let dl = null;
  let loaded;
  if (kept) {
    // Still not in Dropbox (offline, signed out): open the newest version, from this device.
    loaded = { state: kept.state, carried: true };
    toast('Opened your latest changes from this device. They save to Dropbox by themselves when it is reachable.', 7000);
  } else {
    try { dl = await dbx.download(abs(rel)); } catch (err) { if (seq === openSeq) failOpen(rel, err); return; }
    if (seq !== openSeq) return;
    try { loaded = await stateFromBytes(dl.bytes, ext === 'jpg' ? 'jpeg' : ext); } catch (err) { failOpen(rel, err); return; }
  }
  if (seq !== openSeq) return;

  // A JPEG or WebP opens photo-backed; its first save writes a PNG next to it.
  doc = {
    rel: dl?.meta ? relOf(dl.meta.path) : (e?.rel || rel),
    path: dl?.meta?.path || e?.path || abs(rel),
    rev: kept ? kept.baseRev : (dl.meta?.rev || e?.rev || null),
    id: dl?.meta?.id || e?.id || '',
    size: dl ? dl.bytes.length : (e?.size || 0),
    modified: dl?.meta?.modified || e?.modified || 0,
    carried: loaded.carried,
    isPic: kept ? kept.isPic : isPic,
    kept: !!kept,
    drill: { id: rel, name: kept ? kept.name : baseName(rel).replace(/\.(jpe?g|webp)$/i, ''), state: loaded.state },
    frames: { seq: 1, names: [], captions: [] },
  };
  currentFolder = parentOf(doc.rel);

  // Saving is silent (Tony, 2026-09-23): the pill stays in the page, hidden, for the
  // engine's own status text; a failed save says so in a toast, and offline or
  // signed out shows in the status banner.
  status = h('span', { id: 'edStatus', class: 'cthd-badge is-secondary cthd-status', hidden: true, text: 'Saved' });
  topbar = h('div', { class: 'cthw-topbar' });
  const stage = h('div', { class: 'cthd-stage' }, h('div', { class: 'ed' }));
  stage.firstChild.innerHTML = EDITOR_MARKUP;
  // No details panel (Tony, 2026-09-23): every action is in the top bar.
  const grid = h('div', { class: 'cthd-editor is-side-closed', tabindex: '-1' }, stage);
  rail = h('nav', { class: 'cthw-rail', 'aria-label': 'Diagram actions' });
  wireRailTips(rail);
  edRoot = h('div', { class: 'cthw-doc' }, topbar, h('div', { class: 'cthw-docbody' }, grid, rail));
  main.replaceChildren(edRoot);
  paintTop();

  edRoot.addEventListener('pointerdown', (ev) => {
    const t = ev.target;
    if (!(t instanceof HTMLInputElement) && !(t instanceof HTMLTextAreaElement) && !t.closest?.('.cthw-topbar')) grid.focus({ preventScroll: true });
  }, true);

  ed = createEditor({
    root: edRoot,
    layer,
    getSettings: () => settings.engine,
    setSettings: (p) => { Object.assign(settings.engine, p); saveSettings(); },
    isActive: (ev) => !!doc && !isModalOpen() && !document.querySelector('.anim-veil') && !tree.contains(ev.target) && !topbar.contains(ev.target),
    onShortcuts: () => shortcutsModal(),
    saveExport: (blob, name) => exportBlob(blob, name),
  });
  await ed.openEditor(doc.drill, {
    save: (drill) => queueWrite(drill),
    onFrames: (info) => { const was = doc?.frames.seq; if (doc) doc.frames = info; if (was !== info.seq) paintTop(); },
    onRinkSel: () => paintTop(),
    onDirty: (dirty) => { status?.classList.toggle('is-dirty', dirty); status?.classList.toggle('is-saving', !dirty); },
  });
  if (!doc) return;
  doc.frames = ed.frameInfo();
  const bar = edRoot.querySelector('#edBar');
  const tipify = () => bar.querySelectorAll('[data-tip]').forEach((b) => { b.setAttribute('title', b.dataset.tip); b.setAttribute('aria-label', b.dataset.tip); b.removeAttribute('data-tip'); });
  tipify();
  tipObs = new MutationObserver(tipify);
  tipObs.observe(bar, { childList: true, subtree: true });
  paintTop();
  if (!doc.carried) toast(doc.isPic ? 'This is a plain picture, so it opened as a photo to draw on. Your first change saves a diagram PNG beside it.' : 'This picture has no diagram inside yet, so it opened as a photo to draw on. Your first change saves the drawing into the file.', 7000);
  const wrap = edRoot.querySelector('#edStageWrap');
  let raf = 0;
  ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => ed?.refit()); });
  ro.observe(wrap);
  document.title = `${doc.drill.name} · CTH Diagrams`;
  if (doc.kept) setKeptStatus(doc);
  if (pendingRename) { pendingRename = false; requestAnimationFrame(() => startRename()); }
  // Tool keys work at once: after a click in the tree or library, focus sits
  // there, and the editor only takes keys aimed at it.
  else { const a = document.activeElement; if (!a || a === document.body || tree.contains(a) || !edRoot?.contains(a)) grid.focus({ preventScroll: true }); }
}
let waitingFor = null;

function failOpen(rel, err) {
  if (err?.message === 'NOT_CONNECTED') { render(); return; }
  const offline = dbx.isNetworkError(err);
  if (offline) noteTrouble(err);
  main.replaceChildren(h('div', { class: 'cthd-lib' }, h('div', { class: 'cthd-lib-body' }, emptyState(offline ? 'wifi-off' : 'triangle-alert', offline ? 'Offline' : 'Could not open this diagram', offline ? `${baseName(rel)} opens as soon as the connection is back.` : `${rel}: ${err?.message || err}`, button({ label: 'Back to the folder', variant: 'outline', onClick: () => go('f', parentOf(rel)) })))));
}

async function closeDoc(skipFlush = false) {
  if (!ed) { doc = null; return; }
  if (!skipFlush) await flush();
  ro?.disconnect(); ro = null;
  tipObs?.disconnect(); tipObs = null;
  ed.destroy();
  ed = null;
  doc = null;
  edRoot = null; topbar = null; status = null; rail = null; hideRailTip();
  document.title = 'CTH Diagrams';
}

async function flush() {
  if (ed?.isDirty()) { try { await ed.saveNow(); } catch (e) { console.error('[cthd] flush', e); } }
  await writeChain;
}

function queueWrite(drill) {
  const p = writeChain.then(() => write(drill));
  writeChain = p.catch(() => {});
  return p;
}

const draftKeyOf = (rel, isPic) => `${isPic ? 'pic:' : ''}${String(rel).toLowerCase()}`;
const draftKey = (d) => draftKeyOf(d.rel, d.isPic);

/** Render a draft to PNG and put it in Dropbox. Never overwrites a newer file. */
async function uploadDraft(dr) {
  const bytes = await diagramBytes(dr.state, pictureOpts());
  let meta; let copied = false;
  if (dr.isPic) {
    // First save of a JPEG / WebP: the diagram becomes a PNG beside it.
    meta = await dbx.upload(abs(uniqueRel(parentOf(dr.rel), dr.name)), bytes);
  } else {
    try {
      meta = await dbx.upload(abs(dr.rel), bytes, dr.baseRev ? { rev: dr.baseRev } : { overwrite: true });
    } catch (e) {
      if (!(e instanceof dbx.DbxError && /conflict/.test(e.body || ''))) throw e;
      // Changed or deleted somewhere else (another browser or device) since it
      // opened here: never overwrite that. Keep both, and say so.
      meta = await dbx.upload(abs(uniqueRel(parentOf(dr.rel), `${dr.name} (web edit)`)), bytes);
      copied = true;
    }
  }
  await drafts.del(dr.key).catch(() => {});
  noteOk();
  return { meta, copied, bytes };
}
const copiedMsg = (name, meta) => `"${name}" was changed or deleted somewhere else while you edited it here, so your version was saved as "${baseName(meta.name)}". Nothing was lost.`;

function applySaved(d, meta, bytes) {
  d.rel = relOf(meta.path); d.path = meta.path; d.rev = meta.rev; d.id = meta.id || d.id; d.modified = meta.modified; d.carried = true; d.isPic = false; d.kept = false;
  if (bytes) d.size = bytes.length;
  d.drill.id = d.rel;
  addEntry(meta);
  if (doc === d) {
    history.replaceState(null, '', `#/d/${encodeRel(d.rel)}`);
    if (status && !ed?.isDirty()) { status.textContent = 'Saved'; status.classList.remove('is-kept', 'is-dirty', 'is-saving'); status.title = 'Saves itself to Dropbox a second after you stop.'; }
    paintTop();
  }
  renderTree();
}
function setKeptStatus(d) {
  setTimeout(() => { if (doc === d && d.kept && status) { status.textContent = 'On this device'; status.classList.add('is-kept'); status.title = 'Not in Dropbox yet. It saves there by itself when Dropbox is reachable.'; } }, 0);
}

async function write(drill) {
  const d = doc;
  if (!d || d.drill !== drill) return drill;
  writing++;
  // Kept on this device first: whatever happens to the upload, the edit survives a reload.
  const dr = { key: draftKey(d), rel: d.rel, baseRev: d.rev || null, isPic: !!d.isPic, name: d.drill.name, state: drill.state, at: Date.now() };
  try { await drafts.put(dr); } catch (_) { /* private window: the upload is the only copy */ }
  try {
    const wasPic = d.isPic;
    const { meta, copied, bytes } = await uploadDraft(dr);
    if (wasPic) toast(`Saved as ${meta.name}. The original picture is untouched.`);
    if (copied) toast(copiedMsg(d.drill.name, meta), 10000);
    applySaved(d, meta, bytes);
    status?.classList.remove('is-saving');
    return drill;
  } catch (e) {
    status?.classList.remove('is-saving');
    if (dbx.isNetworkError(e) || e.message === 'NOT_CONNECTED') {
      // Offline or signed out: the draft holds it; flushDrafts saves it later.
      noteTrouble(e);
      d.kept = true;
      draftCount = (await drafts.all().catch(() => [])).length;
      updateBanner();
      setKeptStatus(d); // after the last await: it must land after the editor writes "Saved"
      return drill;
    }
    status?.classList.add('is-dirty');
    console.error('[cthd] save to Dropbox', e);
    toast(`Could not save "${d.drill.name}" to Dropbox (${e.message || e}). Your change is kept on this device and saves with your next edit.`, 9000);
    throw e; // the editor says "Not saved" and keeps the edit; the draft is kept too
  } finally {
    writing--;
  }
}

// Save every kept draft, in the write queue so it never races an autosave.
let flushingDrafts = false;
function flushDrafts(manual = false) {
  const p = writeChain.then(() => flushDraftsNow(manual));
  writeChain = p.catch(() => {});
  return p;
}
async function flushDraftsNow(manual) {
  if (flushingDrafts) return;
  const list = await drafts.all().catch(() => []);
  draftCount = list.length;
  if (!list.length || !dbx.connected()) { updateBanner(); if (manual && !list.length) toast('Everything is saved.'); return; }
  flushingDrafts = true;
  try {
    for (const dr of list.sort((a, b) => a.at - b.at)) {
      try {
        const { meta, copied, bytes } = await uploadDraft(dr);
        if (copied) toast(copiedMsg(dr.name, meta), 10000);
        if (doc && draftKey(doc) === dr.key) applySaved(doc, meta, bytes); else addEntry(meta);
      } catch (e) {
        noteTrouble(e);
        if (manual) toast(`Still not saved: ${e.message || e}`, 6000);
        break;
      }
    }
  } finally {
    flushingDrafts = false;
    draftCount = (await drafts.all().catch(() => [])).length;
    updateBanner();
  }
}

/** The tree changed (a long-poll, a refresh): reload the open diagram if someone else saved it. */
async function syncOpenDoc() {
  if (!doc || writing || doc.kept) return;
  let e = find(doc.rel);
  if (!e && doc.id) {
    // renamed or moved elsewhere (Dropbox, another device): follow it by its id
    const m = (entries || []).find((x) => !x.dir && x.id === doc.id);
    if (m) {
      toast(`"${doc.drill.name}" was renamed or moved in Dropbox. Following it to ${m.rel}.`, 6000);
      doc.rel = m.rel; doc.path = m.path; doc.rev = m.rev; doc.drill.name = baseName(m.rel); doc.drill.id = m.rel;
      currentFolder = parentOf(m.rel);
      history.replaceState(null, '', `#/d/${encodeRel(m.rel)}`);
      paintTop(); renderTree();
      return;
    }
  }
  if (!e) {
    if (!doc.goneWarned) { doc.goneWarned = true; toast(`"${doc.drill.name}" was deleted from Dropbox. Keep editing if you like: your next change saves it as a copy.`, 9000); }
    return;
  }
  if (!doc.rev || e.rev === doc.rev) return;
  if (ed?.isDirty()) { if (doc.warnedRev !== e.rev) { doc.warnedRev = e.rev; toast(`"${doc.drill.name}" changed in Dropbox while you were editing it. Your edits are kept and will be saved as a separate copy.`, 8000); } return; }
  const rel = doc.rel;
  await closeDoc(true);
  await openDoc(rel);
  toast(`"${baseName(rel)}" changed in Dropbox and was reloaded.`);
}

function paintTop() {
  if (!topbar || !doc) return;
  if (nameEditing(topbar)) { renderSkipped = true; return; }
  const nav = crumbs(parentOf(doc.rel), doc.drill.name);
  const cur = nav.querySelector('.cthd-crumb.is-current');
  cur.classList.add('is-renamable');
  cur.title = 'Click to rename';
  cur.addEventListener('click', () => startRename());
  topbar.replaceChildren(...[
    treeToggle(),
    reloadBtn(),
    nav,
    h('span', { class: 'cthd-grow' }),
    status].filter(Boolean));
  if (rail) { hideRailTip(); rail.replaceChildren(...editorActions().filter(Boolean)); }
}

// ---- the action rail (right edge of the editor) ---------------------------------------------
// Tony, 2026-09-23: the editor's buttons sit in a slim vertical rail on the
// right, each with an instant shadcn tooltip to its left (no native title
// delay). One tooltip element in the layer serves the whole rail.
let rail = null;
let railTip = null;
function showRailTip(b) {
  const text = b.dataset.railTip;
  if (!text) return;
  if (!railTip) { railTip = h('div', { class: 'cthw-tip', role: 'tooltip' }); layer.append(railTip); }
  railTip.textContent = text;
  railTip.classList.add('is-on');
  const r = b.getBoundingClientRect();
  const t = railTip.getBoundingClientRect();
  railTip.style.left = `${Math.max(8, r.left - t.width - 8)}px`;
  railTip.style.top = `${Math.max(8, Math.min(innerHeight - t.height - 8, r.top + r.height / 2 - t.height / 2))}px`;
}
function hideRailTip() { railTip?.classList.remove('is-on'); }
function wireRailTips(el) {
  el.addEventListener('pointerover', (e) => { const b = e.target.closest?.('[data-rail-tip]'); if (b && el.contains(b)) showRailTip(b); });
  el.addEventListener('pointerout', (e) => { const b = e.target.closest?.('[data-rail-tip]'); if (b && !b.contains(e.relatedTarget)) hideRailTip(); });
  el.addEventListener('pointerdown', hideRailTip);
  el.addEventListener('focusin', (e) => { const b = e.target.closest?.('[data-rail-tip]'); if (b) showRailTip(b); });
  el.addEventListener('focusout', hideRailTip);
}

/**
 * The editor's actions, all in the top bar (there is no details panel). With a
 * rink of a sequence selected, a rink group comes first and the picture, flip
 * and delete actions act on that rink.
 */
function editorActions() {
  const n = doc.frames.seq || 1;
  const rk = scopedRink();
  const scoped = rk != null;
  const acts = ed?.editorActions();
  const act = (iconName, tip, run, opts = {}) => { const b = button({ iconName, tip, size: 'icon-sm', variant: 'ghost', onClick: run, ...opts }); b.removeAttribute('title'); b.dataset.railTip = tip; return b; };
  const entry = () => find(doc.rel) || { rel: doc.rel, path: doc.path, name: `${doc.drill.name}.png` };
  const sep = () => h('span', { class: 'cthw-rail-sep', 'aria-hidden': 'true' });
  const rinkGroup = scoped ? [
    h('span', { class: 'cthw-rink-badge', text: `${rk + 1}/${n}`, 'data-rail-tip': `Rink ${rk + 1} of ${n} is selected: the picture, flip and delete buttons act on it. Escape deselects.` }),
    act('arrow-up', 'Move this rink up', () => acts?.rink.up(rk), { disabled: rk === 0 }),
    act('arrow-down', 'Move this rink down', () => acts?.rink.down(rk), { disabled: rk === n - 1 }),
    act('copy-plus', 'Duplicate this rink (the copy lands under it)', () => acts?.rink.duplicate(rk)),
    act('x', 'Deselect the rink (Escape)', () => acts?.rink.select(null)),
    sep(),
  ] : [];
  const more = (e) => menu(e, [
    n > 1 ? { label: 'Export chosen rinks', icon: 'layers', run: () => exportRinks() } : null,
    { label: scoped ? `Flip rink ${rk + 1}` : 'Flip the selection or the picture', icon: 'flip-horizontal-2', run: () => (scoped ? acts?.rink.flip(rk) : acts?.flipH()) },
    'sep',
    { label: 'Rename', icon: 'pencil', run: () => startRename() },
    { label: 'Move to folder', icon: 'folder-input', run: () => moveFile(entry()) },
    { label: 'Duplicate diagram', icon: 'copy-plus', run: () => duplicateFile(entry()) },
    'sep',
    { label: 'Keyboard shortcuts', icon: 'keyboard', run: () => shortcutsModal() },
  ]);
  return [
    ...rinkGroup,
    act('image', scoped ? `Copy rink ${rk + 1} as a picture` : 'Copy the picture (paste it anywhere)', () => copyPicture()),
    act('download', scoped ? `Download rink ${rk + 1} as a PNG` : 'Download the picture', () => savePicture()),
    act('link', 'Copy Notion embed link', () => copyText(appLink(doc.rel, doc.id), copiedLinkMsg())),
    act('clapperboard', 'Animate: players and pucks follow your arrows', () => animate()),
    sep(),
    act('undo-2', 'Undo (Cmd+Z)', () => acts?.undo()),
    act('redo-2', 'Redo (Shift+Cmd+Z)', () => acts?.redo()),
    act('trash-2', scoped ? `Remove rink ${rk + 1} (Cmd+Z brings it back)` : 'Delete this diagram', () => (scoped ? acts?.rink.remove(rk) : deleteFile(entry())), { danger: true }),
    act('more-horizontal', 'More', (e) => more(e)),
    dbx.embedded ? act('external-link', 'Open in full window', () => openFullWindow()) : null,
  ];
}

const scopedRink = () => { const k = ed?.selectedRink?.(); return k != null && (doc?.frames.seq || 1) > 1 ? k : null; };

/** Rename the open diagram in the top bar (click its name there, or right-click > Rename). */
function startRename() {
  if (!doc || !topbar) return;
  const el = topbar.querySelector('.cthd-crumb.is-current');
  if (el) editName(el, doc.drill.name, renameOpenDoc, 'Diagram name');
}
async function renameOpenDoc(want) {
  const d = doc;
  if (!d) return;
  const clean = safeName(want, d.drill.name);
  if (clean === d.drill.name) return;
  if (d.isPic) { d.drill.name = clean; paintTop(); return; }
  await doRename(find(d.rel) || { rel: d.rel, path: d.path, name: `${d.drill.name}.png` }, clean);
}

async function picture() {
  const rk = scopedRink();
  if (rk != null) return ed.editorActions().rink.canvas(rk);
  await ed.saveNow();
  return ed.pictureCanvas();
}

async function copyPicture() {
  try {
    // Safari wants the ClipboardItem built synchronously from a promise.
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': picture().then(canvasToBlob) })]);
    toast('Picture copied. Paste it anywhere.');
  } catch (e) { if (dbx.embedded) blockedInEmbed('Copying a picture'); else reportError('copy the picture', e); }
}

async function savePicture() {
  const rk = scopedRink();
  const blob = await canvasToBlob(await picture());
  const name = rk != null ? `${doc.drill.name} - ${(doc.frames.names?.[rk] || '').trim() || `rink ${rk + 1}`}.png` : `${doc.drill.name} (picture).png`;
  await exportBlob(blob, name);
}

async function exportRinks() {
  const pick = await rinksModal(doc.frames);
  if (!pick) return;
  await ed.saveNow();
  const all = pick.frames.length === (doc.frames.seq || 1);
  const canvas = await ed.pictureCanvas(all ? null : pick.frames);
  const blob = await canvasToBlob(canvas);
  if (pick.action === 'copy') {
    try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); toast('Picture copied. Paste it anywhere.'); } catch (e) { reportError('copy', e); }
    return;
  }
  await exportBlob(blob, `${doc.drill.name} - rink ${pick.frames.map((k) => k + 1).join('-')}.png`);
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name });
  document.body.append(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 4000);
}

/** Exports (rink pictures, GIFs, videos): a download, or a Dropbox folder (Settings). */
// Inside an embed, downloads may be switched off by the page around it, so
// exports always go to the Dropbox exports folder there.
async function exportBlob(blob, filename) {
  if (settings.exportTo !== 'dropbox' && !dbx.embedded) { saveBlob(blob, filename); toast(`Downloaded ${filename}.`); return; }
  try {
    const dir = dbx.normPath(settings.exportFolder);
    await dbx.ensureFolder(dir);
    const m = filename.match(/^(.*)\.([^.]+)$/);
    let meta;
    for (let n = 1; n < 50 && !meta; n++) {
      try { meta = await dbx.upload(`${dir}/${safeName(m ? m[1] : filename, 'diagram')}${n > 1 ? ` ${n}` : ''}.${m ? m[2] : 'png'}`, new Uint8Array(await blob.arrayBuffer())); }
      catch (e) { if (!(e instanceof dbx.DbxError && /conflict/.test(e.body || ''))) throw e; }
    }
    toast(`Saved ${meta.name} in ${dir}.`, 6000);
  } catch (e) { reportError(`save ${filename}`, e); }
}

async function animate() {
  if (!ed) return;
  try {
    await ed.saveNow();
    const st = ed.currentState();
    await openAnimator({ state: st, name: doc.drill.name, rinkNames: st.rinkNames });
  } catch (e) { reportError('start the animation', e); }
}

function reportError(what, e) {
  console.error(`[cthd] ${what}`, e);
  if (e?.message === 'NOT_CONNECTED') { noteTrouble(e); if (!doc) render(); return; }
  if (dbx.isNetworkError(e)) { noteTrouble(e); toast(`Could not ${what}: no connection. Try again when you are back online.`, 6000); return; }
  toast(`Could not ${what}: ${e?.message || e}`, 7000);
}

// ---- connect and settings --------------------------------------------------------------

let embedWaiting = false;
function embedConnectCard() {
  const code = h('input', { class: 'cthd-input', type: 'text', placeholder: 'Paste the code here', spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Code from the Dropbox window' });
  const finish = async () => {
    try { if (await dbx.finishEmbedAuth(code.value)) { toast('Dropbox connected.'); await startConnected(); } }
    catch (e) { toast(e.message, 7000); }
  };
  code.addEventListener('keydown', (e) => { if (e.key === 'Enter') void finish(); });
  const start = async () => {
    try {
      const r = await dbx.beginEmbedAuth();
      embedWaiting = true; embedUrl = r.url;
      main.replaceChildren(embedConnectCard());
      if (!r.opened) toast('Your browser held back the Dropbox window. Use the "Open Dropbox" link.', 7000);
    } catch (e) { toast(e.message, 6000); }
  };
  const full = h('a', { href: fullWindowUrl(), target: '_blank', rel: 'noopener', text: 'open CTH Diagrams in its own tab' });
  return h('div', { class: 'cthw-connect' },
    h('div', { class: 'cthw-connect-card' },
      h('div', { class: 'cthw-logo is-lg' }, icon('cth-rink')),
      h('h1', { text: 'CTH Diagrams' }),
      embedWaiting
        ? h('div', { class: 'cthw-embed-wait' },
          h('p', { class: 'cthd-hint', text: 'Approve in the Dropbox window. This embed connects by itself when you do.' }),
          h('p', { class: 'cthd-hint', text: 'If Dropbox opened in your browser instead (Notion\'s desktop and phone apps do that), the page it lands on shows a code. Paste it here:' }),
          h('div', { class: 'cthd-row' }, code, button({ label: 'Connect', variant: 'default', onClick: () => void finish() })),
          h('p', { class: 'cthd-hint' }, 'No Dropbox window? ', h('a', { href: embedUrl || '#', target: '_blank', rel: 'opener', text: 'Open Dropbox' }), ', or ', h('button', { class: 'cthw-linkbtn', type: 'button', text: 'start again', onclick: () => void start() }), '.'))
        : h('div', { class: 'cthw-embed-wait' },
          h('p', { class: 'cthd-hint', text: `Your diagrams live in Dropbox at ${dbx.libraryRoot()}. Connect once in Notion, on each device.` }),
          button({ label: 'Connect Dropbox', iconName: 'cloud', variant: 'default', cls: 'is-block', onClick: () => void start() })),
      dbx.storageOk ? null : h('p', { class: 'cthd-hint cthw-warn' }, 'This embed cannot keep your sign-in in this browser, so it asks again each time the page opens. You can also ', full, '.')));
}
let embedUrl = '';

function connectCard() {
  if (dbx.embedded) return embedConnectCard();
  const key = h('input', { class: 'cthd-input', type: 'text', value: dbx.appKey(), placeholder: 'Dropbox app key', spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Dropbox app key' });
  const go = async () => { dbx.setAppKey(key.value); try { await dbx.beginAuth(); } catch (e) { toast(e.message, 6000); } };
  key.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  const uri = dbx.redirectUri();
  return h('div', { class: 'cthw-connect' },
    h('div', { class: 'cthw-connect-card' },
      h('div', { class: 'cthw-logo is-lg' }, icon('cth-rink')),
      h('h1', { text: 'CTH Diagrams' }),
      h('p', { class: 'cthd-hint', text: `Your diagrams live in Dropbox at ${dbx.libraryRoot()}. Connect once on each device.` }),
      button({ label: 'Connect Dropbox', iconName: 'cloud', variant: 'default', cls: 'is-block', onClick: go }),
      h('details', { class: 'cthw-other' },
        h('summary', { text: 'Use a different Dropbox app' }),
        h('p', { class: 'cthd-hint' }, 'Its redirect URIs must include ', h('code', { text: uri }), '.'),
        key)));
}

function settingsModal() {
  const m = modal('Settings', 'New diagram, picture and editor settings follow you to every device and Notion embed through Dropbox. The rest stay in this browser.', { wide: true, focusFirst: false });
  const sec = (title, ...rows) => h('div', { class: 'cthw-set-sec' }, h('div', { class: 'cthw-set-title', text: title }), ...rows);
  const row = (label, hint, control) => h('div', { class: 'cthw-set-row' }, h('div', {}, h('div', { class: 'cthw-set-label', text: label }), hint ? h('div', { class: 'cthd-hint', text: hint }) : null), control);
  const select = (value, opts, on) => {
    const s = h('select', { class: 'cthd-input cthw-select' }, ...opts.map(([v, l]) => h('option', { value: String(v), selected: String(v) === String(value) || null, text: l })));
    s.addEventListener('change', () => on(s.value));
    return s;
  };
  const toggle = (value, on) => {
    const c = h('input', { type: 'checkbox', class: 'cthd-check', checked: value || null });
    c.addEventListener('change', () => on(c.checked));
    return c;
  };
  const text = (value, on, placeholder = '') => {
    const i = h('input', { class: 'cthd-input cthw-text', type: 'text', value, placeholder, spellcheck: 'false' });
    i.addEventListener('change', () => on(i.value));
    return i;
  };
  const acct = h('span', { class: 'cthd-hint', text: dbx.connected() ? 'Connected' : 'Not connected' });
  if (dbx.connected()) dbx.account().then((a) => { acct.textContent = `Connected as ${a.name}${a.email ? ` (${a.email})` : ''}`; }).catch(() => {});

  m.content.append(
    sec('Dropbox',
      row('Account', null, h('div', { class: 'cthd-row' }, acct, dbx.connected()
        ? button({ label: 'Disconnect', iconName: 'log-out', variant: 'outline', size: 'sm', onClick: async () => { await flush(); await dbx.disconnect(); m.close(); render(); } })
        : button({ label: 'Connect', iconName: 'cloud', variant: 'default', size: 'sm', onClick: () => dbx.beginAuth().catch((e) => toast(e.message)) }))),
      row('App key', 'From the Dropbox App Console. Not a secret.', text(dbx.appKey(), (v) => dbx.setAppKey(v))),
      row('Diagrams folder', `The file tree. Default ${dbx.DEFAULT_ROOT} (Obsidian notes embed the pictures from it).`, text(dbx.libraryRoot(), async (v) => { await closeDoc(); dbx.setLibraryRoot(v); entries = null; currentFolder = ''; m.close(); location.hash = '#/'; await refreshTree(); startWatch(); }, dbx.DEFAULT_ROOT))),
    sec('New diagrams',
      row('Name', null, text(settings.newName, (v) => { settings.newName = safeName(v, DEFAULTS.newName); saveSettings(); })),
      row('Nets and goalies', 'Start a new rink with nets and goalies placed.', toggle(settings.newWithFurniture, (v) => { settings.newWithFurniture = v; saveSettings(); }))),
    sec('Pictures',
      row('Picture size', 'How wide the saved PNG is. Smaller files load faster in notes.', select(settings.pictureScale, [[1, 'Full (3200 px)'], [0.75, 'Large (2400 px)'], [0.5, 'Medium (1600 px)']], (v) => { settings.pictureScale = Number(v); saveSettings(); })),
      row('Captions in pictures', 'Rink captions show under each rink in the saved picture.', toggle(settings.engine.captionsInPictures !== false, (v) => { settings.engine.captionsInPictures = v; saveSettings(); })),
      row('Exports go to', 'Rink pictures, GIFs and videos.', select(settings.exportTo, [['download', 'This device (download)'], ['dropbox', 'A Dropbox folder']], (v) => { settings.exportTo = v; saveSettings(); })),
      row('Dropbox exports folder', 'Used when exports go to Dropbox.', text(settings.exportFolder, (v) => { settings.exportFolder = dbx.normPath(v) || DEFAULTS.exportFolder; saveSettings(); }))),
    sec('Look',
      row('Theme', null, select(settings.theme, [['system', 'Match the system'], ['light', 'Light'], ['dark', 'Dark']], (v) => { settings.theme = v; saveSettings(); applyTheme(); })),
      row('Card size', 'Library cards.', select(settings.cardSize, [['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']], (v) => { settings.cardSize = v; saveSettings(); renderMain(); })),
      row('File tree width', 'Drag the right edge of the file tree to resize it; double-click the edge to reset.', button({ label: 'Reset width', variant: 'outline', size: 'sm', onClick: () => setTreeW(DEFAULTS.treeW) }))));
  m.footer(button({ label: 'Done', variant: 'default', onClick: () => m.close() }));
  m.open();
}

// ---- live sync (Dropbox long-poll) --------------------------------------------------------

let watchId = 0;
async function startWatch() {
  const id = ++watchId;
  let cursor = null;
  while (id === watchId && dbx.connected()) {
    try {
      if (!cursor) cursor = await dbx.cursorFor(root());
      const { changes, backoff } = await dbx.longpoll(cursor);
      if (id !== watchId) return;
      if (changes) { cursor = null; await refreshTree({ quiet: true }); }
      if (backoff) await new Promise((r) => setTimeout(r, backoff * 1000));
    } catch (_) {
      // folder missing, offline, or signed out: try again in a while
      cursor = null;
      await new Promise((r) => setTimeout(r, 15000));
    }
  }
}
window.addEventListener('focus', () => { if (dbx.connected()) void refreshTree({ quiet: true }); });

// ---- offline, signed out, waiting changes: one quiet status pill --------------------

let netOk = true;
let signedOut = false;
let draftCount = 0;
function noteOk() { if (!netOk || signedOut) { netOk = true; signedOut = false; updateBanner(); } }
function noteTrouble(e) {
  if (e?.message === 'NOT_CONNECTED') { signedOut = true; updateBanner(); }
  else if (dbx.isNetworkError(e)) { netOk = false; updateBanner(); }
}
const banner = h('div', { class: 'cthw-banner is-hidden', role: 'status', 'aria-live': 'polite' });
function updateBanner() {
  const n = draftCount;
  const waiting = n ? ` ${n} diagram${n === 1 ? ' is' : 's are'} kept on this device.` : '';
  let text = ''; let action = null; let ic = 'cloud';
  if (!navigator.onLine || !netOk) { text = `Offline. Changes save to Dropbox by themselves when you are back online.${waiting}`; ic = 'wifi-off'; }
  else if (signedOut || (!dbx.connected() && (doc || n))) { text = `Dropbox signed you out.${waiting || ' Nothing is lost.'}`; action = ['Reconnect', reconnect]; ic = 'log-in'; }
  else if (n) { text = `${n} diagram${n === 1 ? '' : 's'} waiting to save to Dropbox.`; action = ['Save now', () => void flushDrafts(true)]; ic = 'upload'; }
  banner.replaceChildren();
  banner.classList.toggle('is-hidden', !text);
  if (!text) return;
  banner.append(icon(ic), h('span', { text }));
  if (action) banner.append(h('button', { class: 'cthw-banner-btn', type: 'button', text: action[0], onclick: action[1] }));
}
let leavingForAuth = false;
async function reconnect() {
  try { await flush(); } catch (_) { /* whatever failed is kept on this device */ }
  leavingForAuth = true;
  try { await dbx.beginAuth(); } catch (e) { leavingForAuth = false; toast(e.message, 6000); }
}
setInterval(() => { if (navigator.onLine) void flushDrafts(); }, 20000);
window.addEventListener('online', () => {
  netOk = true; updateBanner(); void refreshTree({ quiet: true }).then(() => { if (!doc && route().kind === 'd') void onRoute(); }); void flushDrafts();
});
window.addEventListener('offline', () => { netOk = false; updateBanner(); });

// Only ask before leaving when an edit is not written anywhere yet (the last
// second before autosave). Saves in flight and kept drafts survive a reload.
window.addEventListener('beforeunload', (e) => {
  if (leavingForAuth) return;
  if (ed?.isDirty()) { void ed.saveNow(); e.preventDefault(); e.returnValue = ''; }
});
// Reopen where you left off: a bare address goes back to the last folder or diagram.
const LAST_KEY = 'cthdw.last.v1';
// (not in an embed: there the embed's own link says what to show)
window.addEventListener('hashchange', () => { if (dbx.embedded) return; try { localStorage.setItem(LAST_KEY, location.hash); } catch (_) { /* fine */ } });
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(); });

// ---- the Dropbox window of an embed sign-in ------------------------------------------------

function relayPage(r) {
  applyTheme();
  document.title = 'Connecting CTH Diagrams';
  const card = h('div', { class: 'cthw-connect-card' }, h('div', { class: 'cthw-logo is-lg' }, icon('cth-rink')));
  if (r.error || !r.code) {
    card.append(h('h1', { text: 'Sign-in cancelled' }), h('p', { class: 'cthd-hint', text: 'Close this window and click Connect Dropbox in Notion to try again.' }));
  } else {
    const copy = button({ label: 'Copy code', iconName: 'copy', variant: r.sent ? 'outline' : 'default', onClick: async () => { try { await navigator.clipboard.writeText(r.code); toast('Code copied. Paste it into the embed in Notion.'); } catch (_) { window.prompt('Copy this code:', r.code); } } });
    card.append(
      h('h1', { text: r.sent ? 'Connected' : 'Almost done' }),
      h('p', { class: 'cthd-hint', text: r.sent ? 'Your Notion embed is connecting now. This window closes by itself.' : 'Copy this code and paste it into the CTH Diagrams embed in Notion. It works once, for a few minutes.' }),
      h('code', { class: 'cthw-code', text: r.code }),
      copy);
    if (r.sent) setTimeout(() => { try { window.close(); } catch (_) { /* stays open */ } }, 1500);
  }
  document.body.append(h('div', { class: 'cthd cthw-app is-disconnected' }, h('main', { class: 'cthw-main' }, h('div', { class: 'cthw-connect' }, card))));
}

// ---- boot ---------------------------------------------------------------------------------

function render() {
  app.classList.toggle('is-tree-closed', !settings.treeOpen);
  if (settings.treeW && settings.treeW !== DEFAULTS.treeW) document.documentElement.style.setProperty('--tree-w', `${settings.treeW}px`);
  if (!dbx.connected()) {
    closeDoc(true);
    app.classList.add('is-disconnected');
    tree.replaceChildren();
    main.replaceChildren(connectCard());
    return;
  }
  app.classList.remove('is-disconnected');
  renderTree();
  renderMain();
}

async function boot() {
  const relay = dbx.relayEmbedCode();
  if (relay) { relayPage(relay); return; }
  if (dbx.embedded) {
    document.documentElement.classList.add('cthw-embed');
    // an embed starts compact: the diagram fills it (both open again with one click)
    if (firstRun) { settings.treeOpen = false; saveSettings(); }
    dbx.onEmbedCode(async (code, state) => {
      try { if (await dbx.finishEmbedAuth(code, state)) { toast('Dropbox connected.'); await startConnected(); } }
      catch (e) { toast(e.message, 7000); }
    });
  }
  if (!location.hash && !dbx.embedded && !new URLSearchParams(location.search).get('code')) { try { const last = localStorage.getItem(LAST_KEY); if (last && last !== '#/') history.replaceState(null, '', `${location.pathname}${last}`); } catch (_) { /* fine */ } }
  applyTheme();
  document.body.append(app, layer);
  app.append(banner);
  setAnimEnv({ layer, openGuide: () => guideModal(GUIDE), saveExport: (blob, name) => exportBlob(blob, name) });
  try {
    await loadAssets({ rink: RINK, shapes: { net: NET, Co: COACH, puck: PUCK, cone: CONE, border: BORDER } });
  } catch (e) {
    main.replaceChildren(emptyState('triangle-alert', 'The rink art could not load', 'Reload the page to try again.'));
    return;
  }
  try { if (await dbx.finishAuth()) toast('Dropbox connected.'); } catch (e) { toast(e.message, 9000); }
  render();
  if (!dbx.connected()) return;
  await startConnected();
}

let started = false;
async function startConnected() {
  if (started) return;
  started = true;
  embedWaiting = false;
  render();
  const r = route();
  currentFolder = r.kind === 'f' ? r.rel : r.kind === 'd' ? parentOf(r.rel) : '';
  await refreshTree();
  await onRoute();
  startWatch();
  void flushDrafts();
  void pullShared();
}

// Exposed for the headless smoke test only.
window.__cthd = { settings, saveSettings, dbx, drafts, pngReadDiagram, get doc() { return doc; } };

boot();
